import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';

import { IconPrinter, IconRefreshCw, IconSearch } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ExcelExportButton } from '../../components/ui/ExcelExportButton';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { exportRowsToExcel, fetchAllPages } from '../../lib/excel-export';
import { useTableSort } from '../../lib/table-sort';
import { fetchBillingList } from '../billing/billing.api';
import type { BillingListFilters, BillingModule, BillingRow } from '../billing/types';
import { formatIpdDateTime } from '../ipd-daycare/ipd-daycare.utils';
import { useIpdDaycareLookup } from '../ipd-daycare/ipd-daycare.queries';
import { ReportAnalyticsGrid, ReportBarChartCard, ReportDonutChartCard, type ReportChartDatum } from './ReportAnalytics';
import { reportChartColor } from './report-chart-colors';
import { emptyDateFilters, todayIso } from './report-filters';

interface DueReportFilters {
  module: '' | BillingModule;
  tpaType: string;
  fromDate: string;
  toDate: string;
}

const moduleOptions: { value: '' | BillingModule; label: string }[] = [
  { value: '', label: 'Select Section' },
  { value: 'opd', label: 'OPD' },
  { value: 'emg', label: 'EMG' },
  { value: 'ipd-daycare', label: 'IPD & DAYCARE' },
  { value: 'dialysis', label: 'DIALYSIS' },
  { value: 'investigation', label: 'INVESTIGATION' },
];

const initialFilters = (): DueReportFilters => ({ module: '', tpaType: '', ...emptyDateFilters() });
const formatAmount = (value: number | null | undefined) => (value ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const upper = (value: string | null | undefined) => value?.trim() ? value.trim().toUpperCase() : '-';
const rowInsuranceType = (row: BillingRow) => upper(row.insuranceType || 'GENERAL');
const formatFilterDate = (value: string) => value ? value.split('-').reverse().join('-') : '';

const PdfIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 2h8l4 4v16H6z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M14 2v5h5M8 16h8M8 12h5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const filterDueRows = (rows: BillingRow[], filters: DueReportFilters, search: string): BillingRow[] => {
  const term = search.trim().toLowerCase();
  return rows
    .filter((row) => row.hasBilling && (row.due ?? 0) > 0)
    .filter((row) => !filters.module || row.module === filters.module)
    .filter((row) => !filters.fromDate || row.billDate.slice(0, 10) >= filters.fromDate)
    .filter((row) => !filters.toDate || row.billDate.slice(0, 10) <= filters.toDate)
    .filter((row) => !filters.tpaType || rowInsuranceType(row) === filters.tpaType)
    .filter((row) => {
      if (!term) return true;
      return [row.billId, row.patientName, row.uhid, row.createdBy, row.moduleLabel, rowInsuranceType(row)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
};

export const DueReportPage = () => {
  const [draft, setDraft] = useState<DueReportFilters>(initialFilters);
  const [filters, setFilters] = useState<DueReportFilters>(draft);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const tpas = useIpdDaycareLookup('tpas');
  const billingFilters: BillingListFilters = {
    page: 1,
    limit: 100,
    dueOnly: true,
    ...(filters.module ? { module: filters.module } : {}),
    ...(filters.fromDate ? { fromDate: filters.fromDate } : {}),
    ...(filters.toDate ? { toDate: filters.toDate } : {}),
  };
  const query = useQuery({
    queryKey: ['billing', 'due-report', billingFilters],
    queryFn: () => fetchAllPages<BillingRow>((page, limit) => fetchBillingList({ ...billingFilters, page, limit }), 100),
  });

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    setFilters(draft);
    setPage(1);
  };

  const reset = () => {
    const next = initialFilters();
    setDraft(next);
    setFilters(next);
    setSearch('');
    setPage(1);
  };

  const rows = useMemo(() => filterDueRows(query.data ?? [], filters, search), [filters, query.data, search]);
  const totals = useMemo(() => rows.reduce((summary, row) => ({
    net: summary.net + (row.grandTotal ?? row.total ?? 0),
    paid: summary.paid + (row.payment ?? 0),
    due: summary.due + (row.due ?? 0),
  }), { net: 0, paid: 0, due: 0 }), [rows]);
  const sectionChartData = useMemo<ReportChartDatum[]>(() => moduleOptions
    .filter((option): option is { value: BillingModule; label: string } => option.value !== '')
    .map((option, index) => {
      const sectionRows = rows.filter((row) => row.module === option.value);
      return {
        name: option.label,
        Net: sectionRows.reduce((sum, row) => sum + (row.grandTotal ?? row.total ?? 0), 0),
        Paid: sectionRows.reduce((sum, row) => sum + (row.payment ?? 0), 0),
        Due: sectionRows.reduce((sum, row) => sum + (row.due ?? 0), 0),
        color: reportChartColor(index),
      };
    }), [rows]);
  const balanceChartData = useMemo<ReportChartDatum[]>(() => [
    { name: 'Paid', value: totals.paid, color: '#0d6b66' },
    { name: 'Due', value: totals.due, color: '#b1424d' },
  ], [totals.due, totals.paid]);
  const tableSort = useTableSort(rows, { patient: (row) => row.patientName });
  const totalPages = Math.max(1, Math.ceil(tableSort.rows.length / pageSize));
  const firstRow = (page - 1) * pageSize;
  const lastRow = Math.min(firstRow + pageSize, tableSort.rows.length);

  useEffect(() => setPage((current) => Math.min(current, totalPages)), [totalPages]);

  const errorMessage = query.error instanceof ApiError ? query.error.message : 'Due report records could not be loaded.';

  const exportToExcel = async () => {
    const exportRows = tableSort.rows;
    const sectionLabel = filters.module ? moduleOptions.find((option) => option.value === filters.module)?.label : 'All sections';
    const period = filters.fromDate || filters.toDate
      ? `${formatFilterDate(filters.fromDate) || 'Earliest'} to ${formatFilterDate(filters.toDate) || 'Latest'}`
      : 'All dates';
    await exportRowsToExcel({
      fileName: `Due-Report-${new Date().toISOString().slice(0, 10)}`,
      title: 'Due Report',
      subtitle: [sectionLabel, period, filters.tpaType || null].filter(Boolean).join(' · '),
      columns: [
        { header: 'SL', width: 6, align: 'right', map: (_row, index) => index + 1 },
        { header: 'Bill ID', width: 14, map: (row) => upper(row.billId) },
        { header: 'Section', width: 18, map: (row) => upper(row.moduleLabel) },
        { header: 'Bill Date', width: 22, map: (row) => formatIpdDateTime(row.billDate) },
        { header: 'Patient Name', width: 30, map: (row) => upper(row.uhid ? `${row.patientName} (${row.uhid})` : row.patientName) },
        { header: 'Insurance Type', width: 18, map: (row) => rowInsuranceType(row) },
        { header: 'Created By', width: 20, map: (row) => upper(row.createdBy) },
        { header: 'Net Amount', width: 14, align: 'right', numFmt: '₹#,##0.00', map: (row) => row.grandTotal ?? row.total ?? 0 },
        { header: 'Paid Amount', width: 14, align: 'right', numFmt: '₹#,##0.00', map: (row) => row.payment ?? 0 },
        { header: 'Due Amount', width: 14, align: 'right', numFmt: '₹#,##0.00', map: (row) => row.due ?? 0 },
      ],
      rows: exportRows,
    });
  };

  return (
    <div className="page-stack collection-reference-page due-report-page">
      <section className="content-card collection-report-card collection-reference-report due-reference-report">
        <header className="general-settings-titlebar"><h2>Due Reports</h2></header>

        <form className="collection-reference-toolbar due-reference-toolbar report-print-hidden" onSubmit={applyFilters}>
          <div className="due-reference-fields">
            <select aria-label="Section" value={draft.module} onChange={(event) => setDraft((current) => ({ ...current, module: event.target.value as DueReportFilters['module'] }))}>
              {moduleOptions.map((option) => <option key={option.value || 'all'} value={option.value}>{option.label}</option>)}
            </select>
            <select aria-label="TPA type" value={draft.tpaType} onChange={(event) => setDraft((current) => ({ ...current, tpaType: event.target.value }))}>
              <option value="">All TPA Types</option>
              {(tpas.data ?? []).map((option) => <option key={option.id} value={option.label.trim().toUpperCase()}>{option.label}</option>)}
            </select>
            <input type="date" value={draft.fromDate} max={draft.toDate || todayIso()} onChange={(event) => setDraft((current) => ({ ...current, fromDate: event.target.value }))} aria-label="From date" />
            <input type="date" value={draft.toDate} min={draft.fromDate || undefined} max={todayIso()} onChange={(event) => setDraft((current) => ({ ...current, toDate: event.target.value }))} aria-label="To date" />
          </div>
          <div className="collection-reference-actions">
            <button className="collection-reference-button search" type="submit"><IconSearch />Search</button>
            <button className="collection-reference-button reset" type="button" onClick={reset}><IconRefreshCw />Reset</button>
            <button className="collection-reference-button print" type="button" onClick={() => window.print()}><IconPrinter />Print</button>
            <ExcelExportButton className="collection-reference-excel" onExport={exportToExcel} disabled={!query.isSuccess} label="Excel" />
            <button className="collection-reference-button pdf" type="button" title="Open the print dialog to save as PDF" onClick={() => window.print()}><PdfIcon />PDF</button>
          </div>
        </form>

        {query.isSuccess ? (
          <ReportAnalyticsGrid className="due-report-analytics">
            <ReportBarChartCard
              eyebrow="Section analysis"
              title="Pending dues by section"
              data={sectionChartData}
              bars={[
                { key: 'Net', name: 'Net', color: '#3777b7' },
                { key: 'Paid', name: 'Paid', color: '#0d6b66' },
                { key: 'Due', name: 'Due', color: '#b1424d' },
              ]}
              valueKind="amount"
            />
            <ReportDonutChartCard eyebrow="Balance mix" title="Paid versus due" data={balanceChartData} valueKind="amount" />
          </ReportAnalyticsGrid>
        ) : null}

        <div className="due-reference-table-section">
          <div className="due-report-table-toolbar report-print-hidden">
            <strong>{rows.length.toLocaleString('en-IN')} due bill{rows.length === 1 ? '' : 's'}</strong>
            <label>
              Search:
              <input value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="Bill, patient, UHID or user" />
            </label>
          </div>

          {query.isLoading ? <LoadingState /> : null}
          {query.isError ? <ErrorState title="Unable to load due report" message={errorMessage} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
          {query.isSuccess && rows.length === 0 ? <EmptyState title="No due bills found" message="Change the filters or date range and search again." /> : null}
          {query.isSuccess && rows.length > 0 ? (
            <div className="due-report-table-scroll">
              <table className="due-report-table">
                <colgroup>
                  <col className="due-col-sl" />
                  <col className="due-col-bill" />
                  <col className="due-col-date" />
                  <col className="due-col-patient" />
                  <col className="due-col-insurance" />
                  <col className="due-col-user" />
                  <col className="due-col-amount" />
                  <col className="due-col-amount" />
                  <col className="due-col-amount" />
                </colgroup>
                <thead>
                  <tr>
                    <th>SL</th>
                    <SortableTh control={tableSort} column="billId">Bill ID</SortableTh>
                    <SortableTh control={tableSort} column="billDate">Bill Date</SortableTh>
                    <SortableTh control={tableSort} column="patient">Patient Name</SortableTh>
                    <th>Insurance Type</th>
                    <SortableTh control={tableSort} column="createdBy">Created By</SortableTh>
                    <SortableTh control={tableSort} column="grandTotal" className="number">Net Amount</SortableTh>
                    <SortableTh control={tableSort} column="payment" className="number">Paid Amount</SortableTh>
                    <SortableTh control={tableSort} column="due" className="number">Due Amount</SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {tableSort.rows.map((row, index) => (
                    <tr className={index >= firstRow && index < lastRow ? undefined : 'due-report-page-row-hidden'} key={`${row.module}-${row.id}`}>
                      <td>{index + 1}</td>
                      <td><strong>{upper(row.billId)}</strong><span className={`due-report-badge ${row.module}`}>{upper(row.moduleLabel)}</span></td>
                      <td>{formatIpdDateTime(row.billDate)}</td>
                      <td>{upper(row.uhid ? `${row.patientName} (${row.uhid})` : row.patientName)}</td>
                      <td>{rowInsuranceType(row)}</td>
                      <td>{upper(row.createdBy)}</td>
                      <td className="number">{formatAmount(row.grandTotal ?? row.total)}</td>
                      <td className="number">{formatAmount(row.payment)}</td>
                      <td className="number due-report-due-value">{formatAmount(row.due)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {query.isSuccess && rows.length > 0 ? (
            <>
              <div className="pagination due-report-pagination report-print-hidden">
                <label>
                  Rows
                  <select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </label>
                <span className="pagination-summary">Showing {(firstRow + 1).toLocaleString('en-IN')} to {lastRow.toLocaleString('en-IN')} of {rows.length.toLocaleString('en-IN')} entries</span>
                <button className="button button-secondary" type="button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>Previous</button>
                <span>Page {page} of {totalPages}</span>
                <button className="button button-secondary" type="button" disabled={page >= totalPages} onClick={() => setPage((current) => current + 1)}>Next</button>
              </div>
              <div className="due-report-total-summary">
                Net Amount : ₹{formatAmount(totals.net)}
                <span>||</span>
                Paid Amount : ₹{formatAmount(totals.paid)}
                <span>||</span>
                Due Amount : <strong>₹{formatAmount(totals.due)}</strong>
              </div>
            </>
          ) : null}
        </div>
      </section>
    </div>
  );
};
